import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

import {
  NEXT_PUBLIC_ARKESTRO_ENV,
  NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
  NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
  NEXT_PUBLIC_DD_VERSION,
  SAMPLE_RATE
} from '@utils/constants';
import { buildDatadogUserTrackingObject } from './utils/buildUserTrackingObject';

class DatadogService {
  static initialize = () => {
    if (!NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN) return;
    if (!NEXT_PUBLIC_DD_RUM_APPLICATION_ID) return;

    DatadogService.connect();
  };

  static connect = () => {
    const baseConfig = {
      env: NEXT_PUBLIC_ARKESTRO_ENV,
      service: 'arkestro-client',
      sessionSampleRate: SAMPLE_RATE,
      site: 'datadoghq.com',
      version: NEXT_PUBLIC_DD_VERSION,
    };

    datadogRum.init({
      ...baseConfig,
      allowedTracingUrls: [/^https:\/\/.*\.arkestro(-[a-zA-Z]*)?\.com(\/.*)?$/],
      applicationId: NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
      clientToken: NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
      compressIntakeRequests: true,
      defaultPrivacyLevel: 'allow',
      enableExperimentalFeatures: ['feature_flags'],
      propagateTraceBaggage: true,
      sessionReplaySampleRate: SAMPLE_RATE,
      silentMultipleInit: true,
      traceContextInjection: 'sampled',
      traceSampleRate: SAMPLE_RATE,
    });

    datadogLogs.init({
      ...baseConfig,
      clientToken: NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
      forwardErrorsToLogs: true,
    });
  };

  static configureUser = user => {
    if (!user) return;

    datadogRum.setUser(buildDatadogUserTrackingObject(user));
    datadogRum.startSessionReplayRecording();
  };

  static captureErrorMessage = (msg, context = {}) => {
    datadogLogs.logger.error(msg, context);
  };

  static captureDebugMessage = (msg, context = {}) => {
    datadogLogs.logger.debug(msg, context);
  };

  // RUM measurement calls. Each one is a no-op before RUM is initialised and never throws, so a
  // measurement can not break the code path it observes.
  static isRumActive = () => {
    try {
      return !!datadogRum.getInitConfiguration();
    } catch {
      return false;
    }
  };

  static safeRum = fn => {
    if (!DatadogService.isRumActive()) return undefined;
    try {
      return fn();
    } catch {
      return undefined;
    }
  };

  // `time` is epoch ms.
  static addTiming = (name, time) => DatadogService.safeRum(() => datadogRum.addTiming(name, time));

  static setViewContextProperty = (key, value) =>
    DatadogService.safeRum(() => datadogRum.setViewContextProperty(key, value));

  // `options.startTime` is epoch ms; `options.duration` is ms.
  static addDurationVital = (name, options) =>
    DatadogService.safeRum(() => datadogRum.addDurationVital(name, options));

  static getViewId = () => DatadogService.safeRum(() => datadogRum.getInternalContext()?.view?.id);
}

export default DatadogService;
